import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { WarehouseDto } from '@/types/organization';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type {
  AbcAnalysisRow,
  CurrentStockRow,
  InventoryValuationRow,
  LowStockRow,
  NearExpiryRow,
  StockAgingRow,
  StockLedgerRow,
  LocationWiseStockRow,
} from '@/types/reports';
import { withId } from './reportHelpers';

// Batch Traceability and Full Traceability moved out to their own module (TraceabilityReportsPage)
// — genealogy/single-batch-history reports are "trace something", not a stock-position report, and
// read oddly bundled in here once there were two of them. See that page for both.
const TABS = ['currentStock', 'locationWiseStock', 'stockLedger', 'valuation', 'lowStock', 'nearExpiry', 'abcAnalysis', 'stockAgingReport'] as const;

const TAB = Object.fromEntries(TABS.map((k, i) => [k, i])) as Record<(typeof TABS)[number], number>;

export default function InventoryReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const { data: processConfiguration } = useProcessConfiguration();
  const showClientColumn = Boolean(processConfiguration?.thirdPartyLogisticsEnabled);
  const [tab, setTab] = useState(0);
  const [warehouseId, setWarehouseId] = useState('');
  const [materialId, setMaterialId] = useState('');

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  const { data: currentStock = [], isLoading: loadingStock } = useQuery({
    queryKey: ['reports', 'current-stock', warehouseId],
    queryFn: async () => (await apiClient.get<CurrentStockRow[]>('/reports/current-stock', { params: { warehouseId: warehouseId || undefined } })).data,
    enabled: tab === TAB.currentStock,
  });
  const { data: stockLedger = [], isLoading: loadingLedger } = useQuery({
    queryKey: ['reports', 'stock-ledger', warehouseId, materialId],
    queryFn: async () =>
      (await apiClient.get<StockLedgerRow[]>('/reports/stock-ledger', { params: { warehouseId: warehouseId || undefined, materialId: materialId || undefined } })).data,
    enabled: tab === TAB.stockLedger,
  });
  const { data: valuation = [], isLoading: loadingValuation } = useQuery({
    queryKey: ['reports', 'inventory-valuation'],
    queryFn: async () => (await apiClient.get<InventoryValuationRow[]>('/reports/inventory-valuation')).data,
    enabled: tab === TAB.valuation,
  });
  const { data: lowStock = [], isLoading: loadingLowStock } = useQuery({
    queryKey: ['reports', 'low-stock'],
    queryFn: async () => (await apiClient.get<LowStockRow[]>('/reports/low-stock')).data,
    enabled: tab === TAB.lowStock,
  });
  const { data: nearExpiry = [], isLoading: loadingNearExpiry } = useQuery({
    queryKey: ['reports', 'near-expiry'],
    queryFn: async () => (await apiClient.get<NearExpiryRow[]>('/reports/near-expiry', { params: { days: 30 } })).data,
    enabled: tab === TAB.nearExpiry,
  });
  const { data: abcAnalysis = [], isLoading: loadingAbc } = useQuery({
    queryKey: ['reports', 'abc-analysis'],
    queryFn: async () => (await apiClient.get<AbcAnalysisRow[]>('/reports/abc-analysis')).data,
    enabled: tab === TAB.abcAnalysis,
  });
  const { data: stockAging = [], isLoading: loadingAging } = useQuery({
    queryKey: ['reports', 'stock-aging'],
    queryFn: async () => (await apiClient.get<StockAgingRow[]>('/reports/stock-aging')).data,
    enabled: tab === TAB.stockAgingReport,
  });
  const { data: locationWiseStock = [], isLoading: loadingLocationStock } = useQuery({
    queryKey: ['reports', 'location-wise-stock', warehouseId, materialId],
    queryFn: async () => (await apiClient.get<LocationWiseStockRow[]>('/reports/location-wise-stock', {
      params: { warehouseId: warehouseId || undefined, materialId: materialId || undefined },
    })).data,
    enabled: tab === TAB.locationWiseStock,
  });

  type LocRow = ReturnType<typeof withId<LocationWiseStockRow>>[number];
  const locationWiseStockColumns: DataTableColumn<LocRow>[] = [
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'zoneName', headerName: t('columns.zone'), render: (r: LocRow) => r.zoneName ?? '—', filterable: true },
    { field: 'locationCode', headerName: t('columns.location'), render: (r: LocRow) => r.locationCode ?? '—', filterable: true },
    { field: 'locationType', headerName: t('columns.locationType'), render: (r: LocRow) => r.locationType ?? '—', filterable: true },
    { field: 'batchNo', headerName: t('columns.batchNo'), render: (r: LocRow) => r.batchNo ?? '—' },
    { field: 'stockStatus', headerName: t('columns.stockStatus'), filterable: true },
    { field: 'quantityOnHand', headerName: t('columns.onHand') },
    { field: 'quantityAllocated', headerName: t('columns.allocated') },
    { field: 'quantityAvailable', headerName: t('columns.available') },
    { field: 'uomCode', headerName: t('columns.uom'), filterable: true },
  ];

  const currentStockColumns: DataTableColumn<ReturnType<typeof withId<CurrentStockRow>>[number]>[] = [
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'quantityOnHand', headerName: t('columns.onHand') },
    { field: 'quantityAllocated', headerName: t('columns.allocated') },
    { field: 'quantityAvailable', headerName: t('columns.available') },
    { field: 'uomCode', headerName: t('columns.uom'), filterable: true },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<CurrentStockRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<CurrentStockRow>>[number]>]
      : []),
  ];

  const stockLedgerColumns: DataTableColumn<ReturnType<typeof withId<StockLedgerRow>>[number]>[] = [
    { field: 'transactionDate', headerName: t('columns.date'), render: (r) => new Date(r.transactionDate).toLocaleString() },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'locationCode', headerName: t('columns.location'), render: (r) => r.locationCode ?? '—' },
    { field: 'batchNo', headerName: t('columns.batch'), render: (r) => r.batchNo ?? '—' },
    { field: 'transactionType', headerName: t('columns.type'), render: (r) => <StatusChip status={r.transactionType} />, filterable: true },
    { field: 'quantityIn', headerName: t('columns.in') },
    { field: 'quantityOut', headerName: t('columns.out') },
    { field: 'balanceQty', headerName: t('columns.balance') },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<StockLedgerRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<StockLedgerRow>>[number]>]
      : []),
  ];

  const valuationColumns: DataTableColumn<ReturnType<typeof withId<InventoryValuationRow>>[number]>[] = [
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'categoryName', headerName: t('columns.category'), render: (r) => r.categoryName ?? '—', filterable: true },
    { field: 'quantityOnHand', headerName: t('columns.onHand') },
    { field: 'averageUnitPrice', headerName: t('columns.avgPrice'), render: (r) => `₹${r.averageUnitPrice.toFixed(2)}` },
    { field: 'stockValue', headerName: t('columns.value'), render: (r) => `₹${r.stockValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
  ];

  const lowStockColumns: DataTableColumn<ReturnType<typeof withId<LowStockRow>>[number]>[] = [
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'quantityOnHand', headerName: t('columns.onHand') },
    { field: 'reorderLevel', headerName: t('columns.reorderLevel') },
    { field: 'safetyStock', headerName: t('columns.safetyStock') },
  ];

  const nearExpiryColumns: DataTableColumn<ReturnType<typeof withId<NearExpiryRow>>[number]>[] = [
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'batchNo', headerName: t('columns.batch') },
    { field: 'expiryDate', headerName: t('columns.expiryDate'), render: (r) => formatDate(r.expiryDate) },
    { field: 'daysToExpiry', headerName: t('columns.daysToExpiry'), render: (r) => r.daysToExpiry ?? '—' },
    { field: 'quantityOnHand', headerName: t('columns.onHand') },
  ];

  const abcColumns: DataTableColumn<ReturnType<typeof withId<AbcAnalysisRow>>[number]>[] = [
    { field: 'abcClass', headerName: t('columns.abcClass'), filterable: true },
    { field: 'materialCount', headerName: t('columns.materialCount') },
    { field: 'stockValue', headerName: t('columns.value'), render: (r) => `₹${r.stockValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
    { field: 'percentOfTotalValue', headerName: t('columns.percentOfTotal'), render: (r) => `${r.percentOfTotalValue}%` },
  ];

  const stockAgingColumns: DataTableColumn<ReturnType<typeof withId<StockAgingRow>>[number]>[] = [
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'locationCode', headerName: t('columns.location'), render: (r) => r.locationCode ?? '—' },
    { field: 'batchNo', headerName: t('columns.batch'), render: (r) => r.batchNo ?? '—' },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<StockAgingRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<StockAgingRow>>[number]>]
      : []),
    { field: 'quantityOnHand', headerName: t('columns.onHand') },
    { field: 'firstReceivedDate', headerName: t('columns.firstReceivedDate'), render: (r) => (r.firstReceivedDate ? formatDate(r.firstReceivedDate) : '—') },
    { field: 'ageDays', headerName: t('columns.ageDays'), render: (r) => r.ageDays ?? '—' },
  ];

  return (
    <>
      <PageHeader title={t('modules.inventory.title')} subtitle={t('modules.inventory.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      {(tab === TAB.currentStock || tab === TAB.stockLedger || tab === TAB.locationWiseStock) && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select size="small" label={t('filters.warehouse')} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">{t('filters.all')}</MenuItem>
            {warehouses.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </TextField>
          {(tab === TAB.stockLedger || tab === TAB.locationWiseStock) && (
            <TextField select size="small" label={t('filters.material')} value={materialId} onChange={(e) => setMaterialId(e.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">{t('filters.all')}</MenuItem>
              {materials.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.code} — {m.name}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      )}

      <Box hidden={tab !== TAB.currentStock}>
        <DataTable columns={currentStockColumns} rows={withId(currentStock, (r) => `${r.materialId}-${r.warehouseId}`)} loading={loadingStock} searchable exportFileName="current-stock" />
      </Box>
      <Box hidden={tab !== TAB.stockLedger}>
        <DataTable columns={stockLedgerColumns} rows={withId(stockLedger, (r) => r.id)} loading={loadingLedger} searchable exportFileName="stock-ledger" />
      </Box>
      <Box hidden={tab !== TAB.valuation}>
        <DataTable columns={valuationColumns} rows={withId(valuation, (r) => r.materialId)} loading={loadingValuation} searchable exportFileName="inventory-valuation" />
      </Box>
      <Box hidden={tab !== TAB.lowStock}>
        <DataTable columns={lowStockColumns} rows={withId(lowStock, (r) => r.materialId)} loading={loadingLowStock} searchable exportFileName="low-stock-reorder" />
      </Box>
      <Box hidden={tab !== TAB.nearExpiry}>
        <DataTable columns={nearExpiryColumns} rows={withId(nearExpiry, (r) => r.batchId)} loading={loadingNearExpiry} searchable exportFileName="near-expiry" />
      </Box>
      <Box hidden={tab !== TAB.abcAnalysis}>
        <DataTable columns={abcColumns} rows={withId(abcAnalysis, (r) => r.abcClass)} loading={loadingAbc} searchable={false} exportFileName="abc-analysis" />
      </Box>
      <Box hidden={tab !== TAB.stockAgingReport}>
        <DataTable
          columns={stockAgingColumns}
          rows={withId(stockAging, (r) => `${r.materialCode}-${r.warehouseName}-${r.locationCode ?? ''}-${r.batchNo ?? ''}`)}
          loading={loadingAging}
          searchable
          exportFileName="stock-aging"
        />
      </Box>
      <Box hidden={tab !== TAB.locationWiseStock}>
        <DataTable
          columns={locationWiseStockColumns}
          rows={withId(locationWiseStock, (r) => `${r.materialId}-${r.locationId ?? 'none'}-${r.batchId ?? 'none'}-${r.stockStatus}`)}
          loading={loadingLocationStock}
          searchable
          exportFileName="location-wise-stock"
        />
      </Box>

    </>
  );
}
